import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { getAiProposedUpdate } from '../../services/api';
import { logoutSuccess } from '../auth/authSlice';
import { nanoid } from 'nanoid';
import { sanitizeMessage, sanitizeInput } from '@/utils/security';
import i18n from '@/i18n';
import { API_BASE_URL } from '@/config/apiBaseUrl';

// Define a type for the message
// Define a type for the message content
export type MessageContent = string | { userFacingMessage: string; internalMessage: unknown };

export type UiPayload = Record<string, unknown>;

export type ChatMode = 'AI' | 'WAITING_FOR_AGENT' | 'HUMAN';

export interface Message {
  id: string;
  author: 'user' | 'ai';
  content: MessageContent;
  uiPayload?: UiPayload;
  suggestedActions?: string[];
  mode?: ChatMode;
}

// Define a type for the slice state
interface ChatState {
  messages: Message[];
  isLoading: boolean;
  mode: ChatMode;
}

// Define the initial state using that type
const initialState: ChatState = {
  messages: [],
  isLoading: false,
  mode: 'AI',
};

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (messageContent: MessageContent, { getState, dispatch }) => {
    // Add user message to the chat history for immediate UI update.
    // The initial empty message should not be added to the history.
    if (messageContent !== '') {
      // Sanitize the message content before adding it to the store
      const sanitizedContent = sanitizeMessage(messageContent);
      dispatch(addMessage({ author: 'user', content: sanitizedContent }));
    }


    const state = getState() as RootState;
    const orderState = state.order;
    const isoLanguage = i18n.language;

    // Build conversation history from current chat state
    const chatState = (getState() as RootState).chat;
    const conversationHistory = chatState.messages.map((m) => ({
      role: m.author === 'ai' ? 'assistant' as const : 'user' as const,
      content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content),
    }));

    const proposed = await getAiProposedUpdate(
      conversationHistory,
      orderState as unknown as Record<string, unknown>,
      isoLanguage,
      { source: 'user' },
    );

    const aiMessageToAdd = {
      author: 'ai' as const,
      content: typeof proposed.utterance === 'string' ? proposed.utterance.trim() : '',
      mode: proposed.mode as ChatMode | undefined,
      suggestedActions: Array.isArray(proposed.suggestedActions)
        ? proposed.suggestedActions.map(sanitizeInput)
        : undefined,
    };

    if (proposed.mode && proposed.mode !== state.chat.mode) {
      dispatch(setChatMode(proposed.mode as ChatMode));
    }

    if (
      (typeof aiMessageToAdd.content === 'string' && aiMessageToAdd.content.length > 0)
      || (Array.isArray(aiMessageToAdd.suggestedActions) && aiMessageToAdd.suggestedActions.length > 0)
    ) {
      dispatch(addMessage(aiMessageToAdd));
    }

    return proposed;
  }
);

export const requestHumanSupport = createAsyncThunk(
  'chat/requestHumanSupport',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { user, temporaryUserId } = state.auth;
    const userId = user?.id;

    const response = await fetch(`${API_BASE_URL}/chat/human-support/request`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, temporaryUserId }),
    });
    const data = await response.json();
    if (data.success) {
      dispatch(setChatMode('WAITING_FOR_AGENT'));
      dispatch(addMessage({
        author: 'ai',
        content: i18n.t('chat.humanSupport.connected', 'You are now connected with a human assistant. They will reply here when a support operator becomes available. Please stay here while the support operator becomes available.'),
      }));
    }
    return data;
  }
);

export const returnToNormalMode = createAsyncThunk(
  'chat/returnToNormalMode',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { user, temporaryUserId } = state.auth;
    const userId = user?.id;

    const response = await fetch(`${API_BASE_URL}/chat/human-support/return`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, temporaryUserId }),
    });
    const data = await response.json();
    if (data.success) {
      dispatch(setChatMode('AI'));
      dispatch(addMessage({
        author: 'ai',
        content: i18n.t('chat.humanSupport.returned', 'Switched back to AI assistant mode.'),
      }));
    }
    return data;
  }
);

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Omit<Message, 'id'>>) => {
      state.messages.push({ id: nanoid(), ...action.payload });
    },
    updateMessage: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<Omit<Message, 'id'>> }>
    ) => {
      const { id, changes } = action.payload;
      const index = state.messages.findIndex((message) => message.id === id);
      if (index !== -1) {
        state.messages[index] = { ...state.messages[index], ...changes };
      }
    },
    clearChat: (state) => {
      state.messages = [];
      state.isLoading = false;
      state.mode = 'AI';
    },
    setChatMode: (state, action: PayloadAction<ChatMode>) => {
      state.mode = action.payload;
    },
    addAdminMessages: (state, action: PayloadAction<Message[]>) => {
      const existingIds = new Set(state.messages.map(m => m.id));
      const newMessages = action.payload.filter(m => !existingIds.has(m.id));
      state.messages.push(...newMessages);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendMessage.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(sendMessage.rejected, (state) => {
        state.isLoading = false;
        // Optionally add an error message to the chat
        state.messages.push({
          id: new Date().toISOString(),
          author: 'ai',
          content: 'Sorry, I encountered an error. Please try again.',
        });
      })
      // Clear chat history on logout to start fresh for a new temporary user
      .addCase(logoutSuccess, (state) => {
        state.messages = [];
        state.isLoading = false;
        state.mode = 'AI';
      });
  },
});

export const { addMessage, updateMessage, clearChat, setChatMode, addAdminMessages } = chatSlice.actions;

export const selectChatMessages = (state: RootState) => state.chat.messages;
export const selectIsChatLoading = (state: RootState) => state.chat.isLoading;
export const selectChatMode = (state: RootState) => state.chat.mode;



export default chatSlice.reducer;
